/* ====================================================================== */
/*  LA DOCUMENTATION D'UN DÉPÔT (Module 5) — lue et écrite À SA SOURCE     */
/* ====================================================================== */
/**
 * Un projet qui livre du logiciel (0076) naît d'`ITS-53/Projet-Modele`, et son
 * dépôt porte la documentation du groupe en Markdown : cadrage, backlog, plans,
 * qualité, conformité, pilotage, documentation du produit. L'outil la MONTRE et
 * la fait ÉDITER, mais ne la RECOPIE pas (règle 5) : chaque lecture va au dépôt,
 * chaque enregistrement est un commit. Un développeur qui édite dans son éditeur
 * et un chef de projet qui lit dans l'outil lisent le même fichier.
 *
 * POURQUOI DANS `shared`. L'API s'en sert pour BORNER ce qu'elle lit et écrit ;
 * l'écran, pour ranger l'arborescence et suivre les liens relatifs d'un
 * document à l'autre. Deux listes auraient divergé, et c'est la borne de
 * sécurité qui aurait menti.
 */
/** Les dossiers du modèle, dans l'ordre du parcours projet (charte, §1). */
export const DOC_SECTIONS = [
    {
        key: 'cadrage',
        label: 'Cadrage',
        intent: 'Cahier des charges, exigences fonctionnelles et non fonctionnelles, risques, traçabilité.',
    },
    { key: 'backlog', label: 'Backlog', intent: 'Epics, user stories et leurs critères d’acceptation.' },
    { key: 'plans', label: "Plans d'implémentation", intent: 'Un plan par story, avant de la coder.' },
    {
        key: 'qualite',
        label: 'Qualité et recette',
        intent: 'Stratégie de test, cas de tests, cahier de recette, définition du « fini ».',
    },
    {
        key: 'conformite',
        label: 'Conformité',
        intent: 'Registre des traitements, grille AIPD, contrôle de sécurité, réversibilité.',
    },
    { key: 'pilotage', label: 'Pilotage', intent: "Plan d'actions, comité de pilotage, journal des décisions." },
    {
        key: 'docs',
        label: 'Documentation du produit',
        intent: 'Architecture, déploiement, guides utilisateur — ce qui décrit le produit fini.',
    },
    { key: '', label: 'Racine du dépôt', intent: 'Présentation, consignes pour ceux qui codent, historique.' },
];
/** Les seuls fichiers de la racine qui relèvent de la documentation. */
export const ROOT_DOC_FILES = ['README.md', 'CLAUDE.md', 'CHANGELOG.md'];
const DOSSIERS = DOC_SECTIONS.map((s) => s.key).filter((k) => k !== '');
/**
 * CE CHEMIN EST-IL UN DOCUMENT ? — la borne de tout le volet.
 *
 * Le jeton d'écriture a accès aux dépôts de l'organisation entiers. Sans cette
 * borne, la route d'enregistrement deviendrait un éditeur universel : un
 * workflow de CI réécrit, un `.env` lu, du code modifié hors revue. Elle ne
 * laisse passer que du Markdown, dans les dossiers du modèle ou parmi trois
 * fichiers de la racine — jamais un segment caché, jamais une remontée `..`.
 */
export function isDocPath(path) {
    if (typeof path !== 'string' || path.length === 0 || path.length > 300)
        return false;
    if (ROOT_DOC_FILES.includes(path))
        return true;
    const segments = path.split('/');
    if (segments.length < 2)
        return false;
    if (!DOSSIERS.includes(segments[0]))
        return false;
    if (!segments.every((s) => /^[\p{L}\p{N}._ -]+$/u.test(s) && !s.startsWith('.')))
        return false;
    return path.toLowerCase().endsWith('.md');
}
/** La section d'un document : son premier dossier, ou la racine. */
export function docSectionOf(path) {
    const premier = path.includes('/') ? path.split('/')[0] : '';
    return (DOC_SECTIONS.find((s) => s.key === premier)?.key ?? '');
}
/**
 * Résout un lien relatif écrit DANS un document (`../cadrage/risques.md#top`)
 * en chemin du dépôt, pour que l'écran suive le lien sans quitter l'outil.
 * Rend `null` pour tout ce qui n'est pas un autre document : une adresse web,
 * une ancre seule, un fichier de code.
 */
export function resolveDocLink(fromPath, href) {
    if (!href || /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('#') || href.startsWith('/')) {
        return null;
    }
    const cible = href.split('#')[0].split('?')[0];
    if (!cible)
        return null;
    const pile = fromPath.split('/').slice(0, -1);
    for (const segment of cible.split('/')) {
        if (segment === '' || segment === '.')
            continue;
        if (segment === '..') {
            if (pile.length === 0)
                return null;
            pile.pop();
        }
        else {
            pile.push(decodeURIComponent(segment));
        }
    }
    const chemin = pile.join('/');
    return isDocPath(chemin) ? chemin : null;
}
/**
 * CE QU'IL RESTE À REMPLIR, selon les marques de la charte documentaire (§2) :
 * une CONSIGNE est un bloc `<!-- COMMENT REMPLIR … -->`, à supprimer une fois la
 * section écrite ; un EMPLACEMENT est un `« … »` court à remplacer. Le second
 * compte est indicatif — le français écrit aussi des guillemets pour citer —,
 * d'où la limite de longueur : un emplacement tient en quelques mots.
 */
export function docFillMarks(text) {
    return {
        consignes: (text.match(/<!--\s*COMMENT REMPLIR/gi) ?? []).length,
        emplacements: (text.match(/«[^»\n]{1,60}»/g) ?? []).length,
        schemas: (text.match(/^```mermaid\s*$/gm) ?? []).length,
    };
}
/**
 * LES POINTS À COMPLÉTER — ce que le RÉDACTEUR a laissé ouvert, par opposition
 * aux consignes de la trame. La charte (§3 du cahier des charges) l'écrit ainsi :
 * « Un champ qu'on ne sait pas remplir s'écrit « À COMPLÉTER — question posée à
 * X le JJ/MM ». Jamais une valeur plausible. » On retient ce marqueur ENTRE
 * GUILLEMETS, et ses cousins que les rédacteurs emploient pour la même chose :
 * à confirmer, à fixer, à définir, à préciser.
 *
 * Partagé entre l'API, qui les recense, et le rendu, qui les surligne : le
 * n-ième point de la liste est le n-ième surligné du document, et c'est ce qui
 * permet d'y sauter directement. Deux détections auraient fini par ne plus
 * compter la même chose, et le saut aurait visé le mauvais endroit.
 */
export const DOC_TODO_RE = /«\s*((?:à|a)\s+(?:compl[ée]ter|confirmer|fixer|d[ée]finir|pr[ée]ciser)\b[^»\n]*?)\s*»/giu;
const cellules = (ligne) => {
    const brut = ligne.trim().replace(/^\|/, '').replace(/\|$/, '');
    return brut.split('|').map((c) => c.trim());
};
const nettoyer = (c) => c.replace(/[*_`]/g, '').replace(/\s+/g, ' ').trim();
/**
 * Recense les points à compléter d'un document, dans L'ORDRE DE LECTURE.
 *
 * Ce qui n'est PAS compté, et pourquoi : les commentaires HTML (les consignes de
 * la trame citent justement le marqueur pour l'expliquer) et les blocs de code
 * (un exemple n'est pas une question ouverte).
 */
export function docTodos(path, text) {
    const documentTitle = docTitle(path, text);
    const out = [];
    let heading = null;
    let dansCommentaire = false;
    let dansCode = false;
    let entete = null;
    const lignes = text.split(/\r?\n/);
    lignes.forEach((brute, i) => {
        let ligne = brute;
        // Les commentaires, sur une ou plusieurs lignes, sont retirés du texte lu.
        if (dansCommentaire) {
            const fin = ligne.indexOf('-->');
            if (fin === -1)
                return;
            dansCommentaire = false;
            ligne = ligne.slice(fin + 3);
        }
        ligne = ligne.replace(/<!--[\s\S]*?-->/g, '');
        const debut = ligne.indexOf('<!--');
        if (debut !== -1) {
            dansCommentaire = true;
            ligne = ligne.slice(0, debut);
        }
        if (/^\s*```/.test(ligne)) {
            dansCode = !dansCode;
            return;
        }
        if (dansCode)
            return;
        // Un marqueur dans un TITRE ou un EN-TÊTE de tableau est rare, mais le rendu
        // le surligne : on le compte aussi, sans quoi les rangs se décaleraient et
        // le saut viserait le point suivant.
        const titre = /^#{1,6}\s+(.+?)\s*#*\s*$/.exec(ligne);
        if (titre) {
            heading = nettoyer(titre[1]);
            entete = null;
        }
        const estTableau = !titre && /^\s*\|/.test(ligne);
        let estEntete = false;
        if (!estTableau)
            entete = null;
        else if (entete === null) {
            // La première ligne d'un tableau en est l'en-tête : elle nomme les colonnes.
            entete = cellules(ligne).map(nettoyer);
            estEntete = true;
        }
        if (estTableau && !estEntete && /^\s*\|?\s*:?-{2,}/.test(ligne))
            return; // la ligne de séparation
        const cells = estTableau && !estEntete ? cellules(ligne) : null;
        for (const m of ligne.matchAll(DOC_TODO_RE)) {
            let contexte = null;
            if (cells) {
                // La cellule du point : autant de colonnes que de « | » avant lui,
                // moins celui qui ouvre la ligne.
                const avant = ligne.slice(0, m.index).replace(/\\\|/g, '');
                const col = Math.max(0, (avant.match(/\|/g) ?? []).length - (/^\s*\|/.test(ligne) ? 1 : 0));
                const etiquette = nettoyer(cells[0] ?? '');
                const colonne = entete?.[col] ?? null;
                contexte =
                    col === 0 ? colonne || null : [etiquette, colonne].filter((x) => x && x.length > 0).join(' · ') || null;
            }
            out.push({
                path,
                documentTitle,
                rang: out.length,
                heading,
                contexte,
                texte: m[1].replace(/\s+/g, ' ').trim(),
                ligne: i + 1,
            });
        }
    });
    return out;
}
/** Le titre d'un document : son premier titre de niveau 1, sinon son nom. */
export function docTitle(path, text) {
    const titre = text?.match(/^#\s+(.+?)\s*#*\s*$/m)?.[1];
    if (titre)
        return titre;
    return path.split('/').pop().replace(/\.md$/i, '');
}
//# sourceMappingURL=repoDocs.js.map